import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { 
  Eye, Shield, Users, FileText, RefreshCw, Save, 
  CheckCircle, AlertTriangle, Info
} from 'lucide-react';
import { toast } from 'sonner';

const WorkerVisibilitySettings = () => {
  const { api, isAdmin } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings/worker-visibility');
      setSettings(response.data);
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to load worker visibility settings');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (isAdmin) {
      fetchSettings();
    }
  }, [isAdmin, fetchSettings]);

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/settings/worker-visibility', settings);
      toast.success('Worker visibility settings saved');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Worker Visibility Settings
            </CardTitle>
            <CardDescription>
              Configure what information support workers can access
            </CardDescription>
          </div>
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            Admin Only
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Team Notes Section */}
        <div className="space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Team Shift Notes
          </h3>
          
          <div className="space-y-4 pl-6">
            {/* Master toggle for team notes */}
            <div className="flex items-start justify-between gap-4 p-3 bg-muted/30 rounded-lg">
              <div className="space-y-1">
                <Label htmlFor="team_notes_enabled" className="font-medium">
                  Enable Team Notes Viewing
                </Label>
                <p className="text-sm text-muted-foreground">
                  Allow workers to view shift notes from team members for their assigned participants
                </p>
              </div>
              <Switch
                id="team_notes_enabled"
                checked={settings?.team_notes_enabled}
                onCheckedChange={() => handleToggle('team_notes_enabled')}
              />
            </div>

            {/* Roster requirement toggle */}
            <div className="flex items-start justify-between gap-4 p-3 bg-muted/30 rounded-lg">
              <div className="space-y-1">
                <Label htmlFor="team_notes_require_roster" className="font-medium">
                  Require Active Roster Assignment
                </Label>
                <p className="text-sm text-muted-foreground">
                  When enabled, workers can only view notes for participants they are currently rostered to support.
                  When disabled, workers can view notes for all participants in their assigned houses.
                </p>
                <div className="flex gap-2 mt-2">
                  {settings?.team_notes_require_roster ? (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      <Shield className="h-3 w-3 mr-1" />
                      More Restrictive
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      <Users className="h-3 w-3 mr-1" />
                      Less Restrictive
                    </Badge>
                  )}
                </div>
              </div>
              <Switch
                id="team_notes_require_roster"
                checked={settings?.team_notes_require_roster}
                onCheckedChange={() => handleToggle('team_notes_require_roster')}
                disabled={!settings?.team_notes_enabled}
              />
            </div>
          </div>
        </div>

        {/* Basic Profile Section */}
        <div className="space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Users className="h-4 w-4" />
            Basic Client Profile
          </h3>
          
          <div className="space-y-4 pl-6">
            <div className="flex items-start justify-between gap-4 p-3 bg-muted/30 rounded-lg">
              <div className="space-y-1">
                <Label htmlFor="basic_profile_enabled" className="font-medium">
                  Enable Basic Profile Access
                </Label>
                <p className="text-sm text-muted-foreground">
                  Allow workers to view essential care information (medications, allergies, emergency contacts) 
                  for their assigned participants
                </p>
                <div className="text-xs text-muted-foreground mt-2 bg-blue-50 dark:bg-blue-950/30 p-2 rounded">
                  <Info className="h-3 w-3 inline mr-1" />
                  Workers will NOT have access to: NDIS plans, funding details, behaviour support plans, or admin documents
                </div>
              </div>
              <Switch
                id="basic_profile_enabled"
                checked={settings?.basic_profile_enabled}
                onCheckedChange={() => handleToggle('basic_profile_enabled')}
              />
            </div>
          </div>
        </div>

        {/* Audit Section */}
        <div className="space-y-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Audit & Compliance
          </h3>
          
          <div className="space-y-4 pl-6">
            <div className="flex items-start justify-between gap-4 p-3 bg-muted/30 rounded-lg">
              <div className="space-y-1">
                <Label htmlFor="audit_all_views" className="font-medium">
                  Log All View Access
                </Label>
                <p className="text-sm text-muted-foreground">
                  Record all team note and basic profile views in the audit trail for NDIS compliance
                </p>
                <div className="text-xs text-muted-foreground mt-2 bg-green-50 dark:bg-green-950/30 p-2 rounded">
                  <CheckCircle className="h-3 w-3 inline mr-1" />
                  Recommended for NDIS compliance
                </div>
              </div>
              <Switch
                id="audit_all_views"
                checked={settings?.audit_all_views}
                onCheckedChange={() => handleToggle('audit_all_views')}
              />
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-4">
          <h4 className="font-medium text-amber-700 dark:text-amber-400 flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4" />
            Privacy Notice
          </h4>
          <p className="text-sm text-amber-600 dark:text-amber-400">
            These settings control worker access to participant information. Changes take effect immediately.
            Access is always governed by roster assignments - workers can only view data for participants 
            they are assigned to support. All access is read-only and can be revoked by removing roster assignments.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button variant="outline" onClick={fetchSettings} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default WorkerVisibilitySettings;
